
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface GuideDialogProps {
  isOpen: boolean;
  onClose: () => void;
  guideId: string; 
  packageId: string;
}

const GuideDialog = ({ isOpen, onClose, guideId, packageId }: GuideDialogProps) => {
  const { t, i18n } = useTranslation(['guides', 'common']);
  const [hasContent, setHasContent] = useState(false);
  
  // Enhanced logging for debugging
  useEffect(() => {
    if (isOpen) {
      console.log('GuideDialog opened with:', { 
        packageId, 
        guideId, 
        language: i18n.language,
        availableNamespaces: i18n.options.ns
      });
    }
  }, [isOpen, packageId, guideId, i18n.language, i18n.options.ns]);
  
  // Check if guide content exists for this package and guide ID with improved logging
  useEffect(() => {
    if (isOpen && packageId && guideId) {
      try {
        // Log the full path we're trying to access
        console.log(`Attempting to access guide at: ${packageId}.${guideId} in language ${i18n.language}`);
        
        // Try to get title first as a simple test
        const title = t(`${packageId}.${guideId}.title`, { ns: 'guides' });
        console.log(`Found title: "${title}"`);
        
        // Now try steps
        const guideSteps = t(`${packageId}.${guideId}.steps`, { returnObjects: true, ns: 'guides' });
        const hasSteps = Array.isArray(guideSteps) && guideSteps.length > 0;
        
        console.log('Guide content found:', {
          hasSteps,
          stepsData: guideSteps,
          guideFullPath: `${packageId}.${guideId}`
        });
        
        setHasContent(hasSteps);
      } catch (error) {
        console.error(`Error accessing guide content for ${packageId}.${guideId} in ${i18n.language}:`, error);
        setHasContent(false);
      }

      // Debug: Check if i18next has loaded the guides namespace for current language
      console.log(`i18next has "${i18n.language}" guides loaded:`, 
        i18n.hasResourceBundle(i18n.language, 'guides'));
    }
  }, [isOpen, packageId, guideId, t, i18n.language, i18n]);

  // Don't render anything if dialog shouldn't be open
  if (!isOpen) {
    return null;
  }

  // Get the guide title, steps and tips safely
  const getGuideTitle = () => {
    try {
      return t(`${packageId}.${guideId}.title`, { ns: 'guides' });
    } catch (error) {
      console.log('Error getting guide title:', error);
      return guideId;
    }
  };

  const getSteps = () => {
    try {
      const steps = t(`${packageId}.${guideId}.steps`, { returnObjects: true, ns: 'guides' });
      return Array.isArray(steps) ? steps : [];
    } catch (error) {
      console.log('Error getting guide steps:', error);
      return [];
    }
  };

  const getTips = () => {
    try {
      const tips = t(`${packageId}.${guideId}.tips`, { returnObjects: true, ns: 'guides' });
      return Array.isArray(tips) ? tips : [];
    } catch (error) {
      console.log('Error getting guide tips:', error);
      return [];
    }
  };

  const guideTitle = getGuideTitle();
  const steps = getSteps();
  const tips = getTips();

  console.log('Rendering guide dialog with:', { 
    guideTitle, 
    stepsCount: steps.length, 
    tipsCount: tips.length,
    language: i18n.language
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center">
            <span className="flex-grow">{t('common.stepByStepGuide', { ns: 'guides' })}: {guideTitle}</span>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full">
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            {t('common.detailedGuideFor', { tool: guideTitle, ns: 'guides' })}
          </DialogDescription>
        </DialogHeader>
        
        {!steps.length ? (
          <div className="py-8 text-center">
            <p className="text-gray-500">
              {t('common.noGuideAvailable', { ns: 'guides' })}
            </p>
          </div>
        ) : (
          <div className="space-y-6 my-4 text-left">
            <div>
              <h3 className="text-lg font-medium text-frikopplad-navy mb-3">{t('common.installationProcess', { ns: 'guides' })}:</h3>
              <ol className="space-y-3 list-decimal pl-5">
                {steps.map((step: string, index: number) => (
                  <li key={index} className="pl-2">
                    {step}
                  </li>
                ))}
              </ol>
            </div>
            
            {/* Only show tips section if there are tips available */}
            {tips.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-frikopplad-navy mb-3">{t('common.tipsForOptimalUse', { ns: 'guides' })}:</h3>
                <ul className="space-y-2 list-disc pl-5">
                  {tips.map((tip: string, index: number) => (
                    <li key={index} className="pl-2">
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close', { ns: 'guides' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GuideDialog;
